/**
 * Email Template Builder Page
 *
 * Split-panel layout matching the PHP builder:
 * - Left: template details form (name, folder, description, subject, preheader, from email/name)
 * - Right: FtpEditor in preview mode (canvas only + slim toolbar)
 * - "Open Editor" expands to fullscreen with full left panel + toolbar
 *
 * New and existing templates use the same FtpEditor split-panel layout.
 * Legacy fallback → HtmlEmailEditor (iframe-based, ?editor=legacy)
 */
import { useCallback, useEffect, useMemo, useState, useRef, Suspense } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, Loader2, Save, History, GitBranch, X, Check, Mail, MailOpen } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { apiClient } from '@/services/apiClient';
import * as marketingSvc from '@/services/marketing.service';
import { FtpTemplatePicker, type FtpTemplateRow } from '@/components/ftp-editor/FtpTemplatePicker';
import EmailEditorChoiceModal from '@/components/email-editor-host/EmailEditorChoiceModal';
import { detectEditorType, type EmailEditorType } from '@/components/email-editor-host/editor-type';
import type { EmailEditorHostHandle } from '@/components/email-editor-host/EmailEditorHost';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

const HtmlEmailEditor = lazyWithRetry(() => import('@/components/email-editor/HtmlEmailEditor'), 'html-email-editor');
const EmailEditorHost = lazyWithRetry(() => import('@/components/email-editor-host/EmailEditorHost'), 'email-editor-host');

interface Sender {
  emailAccountSenderID: number;
  senderName: string;
  email: string;
  status: number;
  default: boolean;
  verified: boolean;
}

interface EmailTemplate {
  emailTemplateId: string;
  name: string;
  subject: string;
  description: string;
  preheader: string;
  fromEmail: string;
  fromName: string;
  templateType: string;
  folder: string;
  active: boolean;
  htmlContent: string;
  plainContent: string;
  jsonContent: string | null;
}

interface TemplateVersion {
  id: number;
  version_number: number;
  is_current: number;
  created_at: string;
}

interface ActivationReviewItem {
  label: string;
  ok: boolean;
  value?: string | null;
  optional?: boolean;
}

function isHtmlContentEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  const noStyle = html.replace(/<style[\s\S]*?<\/style>/gi, '');
  const noHead = noStyle.replace(/<head[\s\S]*?<\/head>/gi, '');
  const stripped = noHead.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  if (stripped.length > 0) return false;
  if (/<img\s/i.test(noHead)) return false;
  if (/<video\s/i.test(noHead)) return false;
  return true;
}

export default function EmailTemplateBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const useLegacyEditor = searchParams.get('editor') === 'legacy';
  const isNew = id === 'new';

  const editorRef = useRef<EmailEditorHostHandle | null>(null);

  const { data: template, isLoading } = useQuery({
    queryKey: ['email-template-full', id],
    queryFn: async () => {
      const res = await apiClient.get(`/marketing/email-templates/${id}/full`);
      return res.data.data as EmailTemplate;
    },
    enabled: !!id && !isNew,
  });

  const { data: senders = [] } = useQuery({
    queryKey: ['marketing-senders'],
    queryFn: async () => {
      const { data } = await apiClient.get('/marketing/settings/senders');
      return (Array.isArray(data) ? data : data?.data ?? []) as Sender[];
    },
  });

  const { data: foldersData } = useQuery({
    queryKey: ['marketing', 'email-template-folders'],
    queryFn: () => marketingSvc.getEmailTemplateFolders(),
  });

  // "general" is always offered; folders are managed from Marketing > Templates.
  const folderOptions = useMemo(() => {
    const opts = [{ value: 'general', label: 'General' }];
    (foldersData ?? []).forEach(({ name: folderName }) => {
      if (folderName.toLowerCase() !== 'general') {
        opts.push({
          value: folderName,
          label: folderName.charAt(0).toUpperCase() + folderName.slice(1),
        });
      }
    });
    return opts;
  }, [foldersData]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [templateType, setTemplateType] = useState('marketing');
  const [folder, setFolder] = useState('general');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [active, setActive] = useState(false);

  // ── Version / dropdown / modal state ──
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<TemplateVersion | null>(null);
  const [versionPreviewHtml, setVersionPreviewHtml] = useState<string | null>(null);
  const [versionPreviewLoading, setVersionPreviewLoading] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);

  // ── Test send modal state ──
  const [showTestModal, setShowTestModal] = useState(false);

  // ── Activation validation modal state ──
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activationReview, setActivationReview] = useState<ActivationReviewItem[]>([]);
  const [activationHasErrors, setActivationHasErrors] = useState(false);

  const [contentEmpty, setContentEmpty] = useState(true);
  const [editorFullscreen, setEditorFullscreen] = useState(false);

  // ── Editor selection (DnD vs HTML) ──
  // The host is uncontrolled; changing editorType / editorSeed remounts it with
  // the content in editorInit (loaded template, applied template, or blank).
  const [editorType, setEditorType] = useState<EmailEditorType>('html');
  const [editorSeed, setEditorSeed] = useState(0);
  const [editorInit, setEditorInit] = useState<{ html: string; plain: string; json: string | null }>({ html: '', plain: '', json: null });
  const [showEditorChoice, setShowEditorChoice] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // ── Unsaved changes tracking ──
  const [initialized, setInitialized] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const editorReadyRef = useRef(false);
  const saveMenuRef = useRef<HTMLDivElement>(null);

  // Re-seeding bumps editorSeed, which changes the host's key and REMOUNTS the
  // editor — dropping the user out of fullscreen and losing their scroll and
  // selection. That is right on a genuine load and wrong on every other refetch:
  // saving invalidates this query, so an unguarded re-seed slammed the fullscreen
  // editor shut the moment you hit Save. Seed on a template we haven't seeded yet,
  // or when something asked for it explicitly (a version restore), never on a
  // refetch of content the editor already holds.
  const seededTemplateRef = useRef<string | null>(null);
  const forceReseedRef = useRef(false);

  useEffect(() => {
    if (!template) return;
    setName(template.name || '');
    setDescription(template.description || '');
    setSubject(template.subject || '');
    setPreheader(template.preheader || '');
    setTemplateType(template.templateType || 'marketing');
    setFolder(template.folder || 'general');
    setFromEmail(template.fromEmail || '');
    setFromName(template.fromName || '');
    setActive(template.active);
    setContentEmpty(isHtmlContentEmpty(template.htmlContent));
    setFormDirty(false);
    setInitialized(true);

    // Typed string here but sent as an int by the API, so compare as strings.
    const templateKey = String(template.emailTemplateId);
    if (seededTemplateRef.current === templateKey && !forceReseedRef.current) return;
    seededTemplateRef.current = templateKey;
    forceReseedRef.current = false;

    // Open the editor the template was authored with, seeded from its content.
    setEditorType(detectEditorType(template.jsonContent));
    setEditorInit({ html: template.htmlContent || '', plain: template.plainContent || '', json: template.jsonContent ?? null });
    setEditorSeed((s) => s + 1);
    editorReadyRef.current = false;
    const timer = setTimeout(() => { editorReadyRef.current = true; }, 800);
    return () => clearTimeout(timer);
  }, [template]);

  const hasUnsavedChanges = useMemo(() => {
    if (isNew || !template || !initialized) return false;
    if (formDirty) return true;
    if (name !== (template.name || '')) return true;
    if (description !== (template.description || '')) return true;
    if (subject !== (template.subject || '')) return true;
    if (preheader !== (template.preheader || '')) return true;
    if (templateType !== (template.templateType || 'marketing')) return true;
    if (folder !== (template.folder || 'general')) return true;
    if (fromEmail !== (template.fromEmail || '')) return true;
    if (fromName !== (template.fromName || '')) return true;
    if (active !== template.active) return true;
    return false;
  }, [isNew, formDirty, template, initialized, name, description, subject, preheader, templateType, folder, fromEmail, fromName, active]);

  // ── Navigation guards ──
  useUnsavedChangesGuard(hasUnsavedChanges);

  // ── Close save dropdown on outside click ──
  useEffect(() => {
    if (!showSaveMenu) return;
    const handler = (e: MouseEvent) => {
      if (saveMenuRef.current && !saveMenuRef.current.contains(e.target as Node)) {
        setShowSaveMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSaveMenu]);

  const saveMut = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isNew) {
        return apiClient.post('/marketing/email-templates', payload);
      }
      return apiClient.put(`/marketing/email-templates/${id}`, payload);
    },
    onSuccess: (res) => {
      toast.success('Template saved');
      setFormDirty(false);
      editorRef.current?.markSaved();
      qc.invalidateQueries({ queryKey: ['marketing', 'email-templates'] });
      qc.invalidateQueries({ queryKey: ['email-template-full', id] });
      if (isNew && res.data?.data?.emailTemplateId) {
        const newId = String(res.data.data.emailTemplateId);
        // The live editor already holds exactly what was just saved, so mark this
        // template seeded: re-seeding after the navigate would remount the editor
        // and drop the user out of fullscreen right after their first save. It is
        // mounted and settled, so its change tracking is on from here too.
        seededTemplateRef.current = newId;
        editorReadyRef.current = true;
        navigate(`/marketing/templates/email/${newId}`, { replace: true });
      }
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: { message?: string }; message?: string } } })
        ?.response?.data?.error?.message
        || (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Failed to save template';
      toast.error(msg);
    },
  });

  const handleSave = useCallback((saveAsNewVersion = false) => {
    const htmlContent = editorRef.current?.getContent() ?? '';
    const plainContent = editorRef.current?.getPlainContent() ?? '';
    const jsonContent = editorRef.current?.getJson() ?? null;

    saveMut.mutate({
      name: name || 'Untitled Template',
      description,
      subject,
      preheader,
      templateType,
      folder,
      fromEmail,
      fromName,
      active,
      htmlContent,
      plainContent,
      jsonContent,
      ...(saveAsNewVersion ? { saveAsNewVersion: true } : {}),
    });
  }, [name, description, subject, preheader, templateType, folder, fromEmail, fromName, active, saveMut]);

  const handleEditorSave = useCallback((_html: string, _plain: string, _json: string | null) => {
    handleSave();
  }, [handleSave]);

  const handleEditorSaveAsNewVersion = useCallback((_html: string, _plain: string, _json: string | null) => {
    handleSave(true);
  }, [handleSave]);

  const handleBack = useCallback(() => {
    navigate('/marketing/templates');
  }, [navigate]);

  // ── Editor fullscreen change tracking ──
  const fsTransitionRef = useRef(false);
  const handleFullscreenChange = useCallback((isFs: boolean) => {
    setEditorFullscreen(isFs);
    if (!isFs) {
      fsTransitionRef.current = true;
      setTimeout(() => {
        fsTransitionRef.current = false;
        const html = editorRef.current?.getContent() ?? '';
        setContentEmpty(isHtmlContentEmpty(html));
      }, 300);
    }
  }, []);

  // ── Editor content change tracking ──
  const formDirtyRef = useRef(false);
  formDirtyRef.current = formDirty;
  // getContent() compiles the whole email, and the editors report every
  // keystroke, so only the dirty flag is set inline — the empty-content check
  // trails behind it.
  const contentEmptyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleEditorChange = useCallback(() => {
    if (!editorReadyRef.current) return;
    if (!formDirtyRef.current) setFormDirty(true);
    if (fsTransitionRef.current) return;
    if (contentEmptyTimerRef.current) clearTimeout(contentEmptyTimerRef.current);
    contentEmptyTimerRef.current = setTimeout(() => {
      const html = editorRef.current?.getContent() ?? '';
      setContentEmpty(isHtmlContentEmpty(html));
    }, 400);
  }, []);

  useEffect(() => () => {
    if (contentEmptyTimerRef.current) clearTimeout(contentEmptyTimerRef.current);
  }, []);

  const handleTemplateApplyCallback = useCallback((_tmpl: FtpTemplateRow) => {
    setContentEmpty(false);
    setFormDirty(true);
  }, []);

  // ── Start-from-scratch: pick DnD vs HTML, then mount a blank editor ──
  const handleEditorChoice = useCallback((type: EmailEditorType) => {
    setShowEditorChoice(false);
    setEditorType(type);
    setEditorInit({ html: '', plain: '', json: null });
    setEditorSeed((s) => s + 1);
    setContentEmpty(false);
    setFormDirty(true);
    // Both editors open in preview mode; jump straight into the full editor so a
    // blank email is immediately buildable (esp. DnD, which needs the palette).
    setTimeout(() => editorRef.current?.openFullscreen(), 100);
  }, []);

  // ── Choose Template: editor follows the picked template's type ──
  const handleTemplatePicked = useCallback((tmpl: FtpTemplateRow) => {
    setShowTemplatePicker(false);
    const json = tmpl.json_content ?? null;
    setEditorType(detectEditorType(json));
    setEditorInit({ html: tmpl.html_content || '', plain: tmpl.plain_content || '', json });
    setEditorSeed((s) => s + 1);
    setContentEmpty(false);
    setFormDirty(true);
  }, []);

  // ── Activation validation ──
  const gatherActivationReview = useCallback((): { items: ActivationReviewItem[]; hasErrors: boolean } => {
    const htmlContent = editorRef.current?.getContent() ?? '';
    const plainContent = editorRef.current?.getPlainContent() ?? '';
    const htmlLower = htmlContent.toLowerCase();
    const plainLower = plainContent.toLowerCase();

    const items: ActivationReviewItem[] = [
      { label: 'Template Name', ok: !!name.trim(), value: name.trim() || null },
      { label: 'Subject', ok: !!subject.trim(), value: subject.trim() || null },
      { label: 'Preheader', ok: !!preheader.trim(), value: preheader.trim() || null },
      { label: 'From Name', ok: !!fromName.trim(), value: fromName.trim() || null },
      { label: 'From Email', ok: !!fromEmail.trim(), value: fromEmail.trim() || null },
      { label: 'HTML Content', ok: !!htmlContent.trim() },
      { label: 'Plain Text Content', ok: !!plainContent.trim() },
      { label: 'Unsubscribe in HTML', ok: htmlLower.includes('{{unsubscribe_link}}') || htmlLower.includes('{{unsubscribe}}') },
      { label: 'Unsubscribe in Plain Text', ok: plainLower.includes('{{unsubscribe_link}}') || plainLower.includes('{{unsubscribe}}') },
      { label: 'Weblink in HTML', ok: htmlLower.includes('{{weblink}}'), optional: true },
      { label: 'Weblink in Plain Text', ok: plainLower.includes('{{weblink}}'), optional: true },
    ];

    const hasErrors = items.some((i) => !i.ok && !i.optional);
    return { items, hasErrors };
  }, [name, subject, preheader, fromName, fromEmail]);

  const handleActiveToggle = useCallback((checked: boolean) => {
    if (checked) {
      const { items, hasErrors } = gatherActivationReview();
      setActivationReview(items);
      setActivationHasErrors(hasErrors);
      setShowActivateModal(true);
    } else {
      setActive(false);
    }
  }, [gatherActivationReview]);

  const confirmActivate = useCallback(() => {
    setShowActivateModal(false);
    setActive(true);
    const htmlContent = editorRef.current?.getContent() ?? '';
    const plainContent = editorRef.current?.getPlainContent() ?? '';
    const jsonContent = editorRef.current?.getJson() ?? null;
    saveMut.mutate({
      name: name || 'Untitled Template',
      description, subject, preheader, templateType, folder, fromEmail, fromName,
      active: true, htmlContent, plainContent, jsonContent,
    });
  }, [name, description, subject, preheader, templateType, folder, fromEmail, fromName, saveMut]);

  // ── Version history ──
  const loadVersions = useCallback(async () => {
    if (!id || isNew) return;
    setVersionsLoading(true);
    try {
      const res = await apiClient.get(`/marketing/email-templates/${id}/versions`);
      setVersions(res.data.data ?? []);
    } catch {
      toast.error('Failed to load versions');
    } finally {
      setVersionsLoading(false);
    }
  }, [id, isNew, toast]);

  const openVersionModal = useCallback(() => {
    setShowVersionModal(true);
    setSelectedVersion(null);
    setVersionPreviewHtml(null);
    loadVersions();
  }, [loadVersions]);

  const selectVersion = useCallback(async (v: TemplateVersion) => {
    setSelectedVersion(v);
    setVersionPreviewLoading(true);
    setVersionPreviewHtml(null);
    try {
      const res = await apiClient.get(`/marketing/email-templates/versions/${v.id}/preview`);
      setVersionPreviewHtml(res.data.data?.htmlContent ?? '');
    } catch {
      setVersionPreviewHtml(null);
      toast.error('Failed to load version preview');
    } finally {
      setVersionPreviewLoading(false);
    }
  }, [toast]);

  const restoreVersion = useCallback(async () => {
    if (!selectedVersion || selectedVersion.is_current) return;
    if (!window.confirm(`Restore Version ${selectedVersion.version_number}? This will replace the current template content.`)) return;
    setRestoringVersion(true);
    try {
      await apiClient.post(`/marketing/email-templates/versions/${selectedVersion.id}/restore`);
      toast.success(`Version ${selectedVersion.version_number} restored`);
      setShowVersionModal(false);
      setFormDirty(false);
      // The whole point is to replace what the editor is showing, so this is the
      // one refetch that must re-seed it.
      forceReseedRef.current = true;
      qc.invalidateQueries({ queryKey: ['email-template-full', id] });
    } catch {
      toast.error('Failed to restore version');
    } finally {
      setRestoringVersion(false);
    }
  }, [selectedVersion, id, toast, qc]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (useLegacyEditor && !isNew) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-screen bg-white"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>}>
        <HtmlEmailEditor
          initialHtml={template!.htmlContent}
          onSave={(html, plain) => saveMut.mutate({ htmlContent: html, plainContent: plain })}
          onBack={handleBack}
          templateName={template?.name || 'Email Template'}
        />
      </Suspense>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="h-5 w-px bg-gray-200" />
          <h1 className="text-base font-semibold text-gray-800 truncate max-w-xs">
            {isNew ? 'New Template' : (name || 'Email Template')}
          </h1>

          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && (
            <div className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Send Test — needs a saved template to send against */}
          {!isNew && (
            <button
              type="button"
              onClick={() => setShowTestModal(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Send Test
            </button>
          )}

          {/* Versions button (existing templates only) */}
          {!isNew && (
            <button
              type="button"
              onClick={openVersionModal}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <History className="h-4 w-4" />
              Versions
            </button>
          )}

          {/* Save button — dropdown for existing templates, simple button for new */}
          {!isNew ? (
            <div className="relative" ref={saveMenuRef}>
              <button
                type="button"
                onClick={() => setShowSaveMenu((v) => !v)}
                disabled={saveMut.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <Save className="h-4 w-4" />
                {saveMut.isPending ? 'Saving…' : 'Save Changes'}
                <ChevronDown className="h-4 w-4 -mr-1" />
              </button>
              {showSaveMenu && (
                <div className="absolute right-0 z-50 mt-1 w-52 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => { setShowSaveMenu(false); handleSave(false); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Save className="h-4 w-4" />
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowSaveMenu(false); handleSave(true); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <GitBranch className="h-4 w-4" />
                    Save as New Version
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => handleSave(false)}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <Save className="h-4 w-4" />
              {saveMut.isPending ? 'Saving…' : 'Create Template'}
            </button>
          )}
        </div>
      </div>

      {/* Split panel */}
      <div className="flex flex-1 min-h-0">
        {/* Left: details form */}
        <div className="w-[42%] min-w-[400px] max-w-[560px] overflow-y-auto border-r border-gray-200 bg-white">
          <div className="p-5 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Template Details</h2>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => handleActiveToggle(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
                <span className="ms-2 text-sm text-gray-600">{active ? 'Active' : 'Inactive'}</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Template Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                placeholder="Enter template name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Folder</label>
              <div className="relative">
                <select
                  value={folder}
                  onChange={(e) => setFolder(e.target.value)}
                  className="w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none bg-white pr-8"
                >
                  {folderOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Template Type</label>
              <div className="relative">
                <select
                  value={templateType}
                  onChange={(e) => setTemplateType(e.target.value)}
                  className="w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none bg-white pr-8"
                >
                  <option value="marketing">Marketing</option>
                  <option value="transactional">Transactional</option>
                  <option value="automation">Automation</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                placeholder="Optional description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Email Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                placeholder="Subject line"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Preheader</label>
              <input
                type="text"
                value={preheader}
                onChange={(e) => setPreheader(e.target.value)}
                maxLength={100}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                placeholder="Preview text shown after the subject line in the inbox"
              />
              <p className="mt-1 text-xs text-gray-400">Preview text shown after the subject line in most email clients. Ideal length is 40 to 100 characters.</p>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-600 mb-1">From Email</label>
                <div className="relative">
                  <select
                    value={fromEmail}
                    onChange={(e) => {
                      setFromEmail(e.target.value);
                      const s = senders.find((s) => s.email === e.target.value);
                      setFromName(s?.senderName || '');
                    }}
                    className="w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none bg-white pr-8"
                  >
                    <option value="">-- Select sender email --</option>
                    {senders.map((s) => (
                      <option key={s.emailAccountSenderID} value={s.email}>
                        {s.email} ({s.senderName})
                      </option>
                    ))}
                    {fromEmail && !senders.some((s) => s.email === fromEmail) && (
                      <option value={fromEmail} className="text-red-500">
                        {fromEmail} (Not in Account Senders)
                      </option>
                    )}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-600 mb-1">From Name</label>
                <input
                  type="text"
                  value={fromName}
                  readOnly
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-50 text-gray-500 outline-none cursor-default"
                  placeholder="Selected from sender"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right: Editor preview + empty overlay */}
        <div className="flex-1 min-w-0 bg-[#1e1e2e] relative">
          {/* Editor host — HTML or Drag & Drop, selected per template/choice */}
          <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>}>
            <EmailEditorHost
              key={`${id}-${editorType}-${editorSeed}`}
              ref={editorRef}
              editorType={editorType}
              initialHtml={editorInit.html}
              initialPlainContent={editorInit.plain}
              initialJson={editorInit.json}
              onSave={handleEditorSave}
              onSaveAsNewVersion={!isNew ? handleEditorSaveAsNewVersion : undefined}
              onChange={handleEditorChange}
              onFullscreenChange={handleFullscreenChange}
              onTemplateApply={handleTemplateApplyCallback}
              previewMode
            />
          </Suspense>

          {/* Empty content overlay */}
          {contentEmpty && !editorFullscreen && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#1e1e2e]/80 backdrop-blur-sm">
              <div className="text-center max-w-sm">
                <MailOpen className="h-14 w-14 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Email Content is Empty</h3>
                <p className="text-sm text-slate-400 mb-6">Start building your email from scratch or choose a template</p>
                <div className="flex items-center justify-center gap-3">
                  <button type="button" onClick={() => setShowTemplatePicker(true)}
                    className="rounded-md border border-slate-500 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition-colors">
                    Choose Template
                  </button>
                  <button type="button"
                    onClick={() => setShowEditorChoice(true)}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors">
                    Start Building
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Editor choice + template picker (start-from-scratch / choose template) ── */}
      <EmailEditorChoiceModal
        isOpen={showEditorChoice}
        onClose={() => setShowEditorChoice(false)}
        onChoose={handleEditorChoice}
      />
      <FtpTemplatePicker
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        mode="select"
        onApply={() => {}}
        onSelect={(t) => handleTemplatePicked(t as FtpTemplateRow)}
      />

      {/* ── Activation Validation Modal ── */}
      {showActivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="flex items-center gap-2">
                {activationHasErrors ? (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
                    <X className="h-4 w-4 text-red-600" />
                  </div>
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                )}
                <h3 className="text-lg font-semibold text-gray-800">
                  {activationHasErrors ? 'Cannot Activate Template' : 'Activate Template'}
                </h3>
              </div>
              <button type="button" onClick={() => setShowActivateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              <p className="mb-4 text-sm text-gray-600">
                {activationHasErrors
                  ? 'The following issues must be resolved before this template can be made active:'
                  : 'All required fields are complete. Review the details below and confirm activation.'}
              </p>

              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Template Details</div>
              <ul className="mb-4 space-y-1.5">
                {activationReview.filter((r) => ['Template Name', 'Subject', 'Preheader', 'From Name', 'From Email'].includes(r.label)).map((r) => (
                  <li key={r.label} className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      {r.ok
                        ? <Check className="h-4 w-4 text-green-500" />
                        : <X className="h-4 w-4 text-red-500" />}
                      <span className="text-gray-700">{r.label}</span>
                    </div>
                    <span className={r.ok ? 'text-gray-500 truncate max-w-[200px]' : 'text-red-500 italic'}>
                      {r.ok ? (r.value || '') : 'Missing'}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Content</div>
              <ul className="mb-4 space-y-1.5">
                {activationReview.filter((r) => ['HTML Content', 'Plain Text Content'].includes(r.label)).map((r) => (
                  <li key={r.label} className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      {r.ok
                        ? <Check className="h-4 w-4 text-green-500" />
                        : <X className="h-4 w-4 text-red-500" />}
                      <span className="text-gray-700">{r.label}</span>
                    </div>
                    <span className={r.ok ? 'text-green-600 text-xs' : 'text-red-500 text-xs italic'}>
                      {r.ok ? 'Included' : 'Not included'}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Variables</div>
              <ul className="space-y-1.5">
                {activationReview.filter((r) => r.label.startsWith('Unsubscribe') || r.label.startsWith('Weblink')).map((r) => (
                  <li key={r.label} className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      {r.optional
                        ? <span className="h-4 w-4 text-center text-gray-400">—</span>
                        : r.ok
                          ? <Check className="h-4 w-4 text-green-500" />
                          : <X className="h-4 w-4 text-red-500" />}
                      <span className="text-gray-700">
                        {r.label}
                        {r.optional && <span className="ml-1 text-xs text-gray-400">(optional)</span>}
                      </span>
                    </div>
                    <span className={
                      r.optional
                        ? 'text-gray-400 text-xs'
                        : r.ok ? 'text-green-600 text-xs' : 'text-red-500 text-xs italic'
                    }>
                      {r.ok ? 'Included' : (r.optional ? 'Not included' : 'Not included')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
              {activationHasErrors ? (
                <button
                  type="button"
                  onClick={() => setShowActivateModal(false)}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  OK
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setShowActivateModal(false)}
                    className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmActivate}
                    className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors"
                  >
                    Activate &amp; Save
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Version History Modal ── */}
      {showVersionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex h-[80vh] w-full max-w-4xl flex-col rounded-lg bg-[#1e1e2e] shadow-xl border border-[#313244]">
            {/* Modal header */}
            <div className="flex items-center justify-between border-b border-[#313244] px-5 py-3 shrink-0">
              <div className="flex items-center gap-2 text-[#cdd6f4]">
                <History className="h-4 w-4" />
                <span className="text-sm font-semibold">Version History</span>
              </div>
              <button type="button" onClick={() => setShowVersionModal(false)} className="text-[#6c7086] hover:text-[#f38ba8] text-xl leading-none">&times;</button>
            </div>

            {/* Modal body — split panes */}
            <div className="flex flex-1 min-h-0">
              {/* Left: version list */}
              <div className="w-64 shrink-0 overflow-y-auto border-r border-[#313244] p-3 space-y-2">
                {versionsLoading ? (
                  <div className="flex flex-col items-center justify-center py-10 text-[#6c7086]">
                    <Loader2 className="h-6 w-6 animate-spin mb-2" />
                    <span className="text-xs">Loading versions...</span>
                  </div>
                ) : versions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-[#6c7086] text-center">
                    <History className="h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">No Version History</p>
                    <p className="text-xs mt-1 opacity-70">Versions appear here when you use &quot;Save as New Version&quot;.</p>
                  </div>
                ) : versions.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => selectVersion(v)}
                    className={`w-full rounded-md border px-3 py-2.5 text-left transition-colors ${
                      selectedVersion?.id === v.id
                        ? 'border-[#89b4fa] bg-[#89b4fa]/10'
                        : 'border-[#313244] hover:border-[#45475a] hover:bg-[#313244]/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#cdd6f4]">Version {v.version_number}</span>
                      {!!v.is_current && (
                        <span className="rounded-full bg-[#89b4fa]/20 px-2 py-0.5 text-[10px] font-semibold text-[#89b4fa]">Current</span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-[#6c7086]">
                      {new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {' at '}
                      {new Date(v.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </button>
                ))}
              </div>

              {/* Right: version preview */}
              <div className="flex-1 min-w-0 flex flex-col">
                {versionPreviewLoading ? (
                  <div className="flex flex-1 items-center justify-center text-[#6c7086]">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : versionPreviewHtml !== null ? (
                  <iframe
                    srcDoc={versionPreviewHtml}
                    className="flex-1 w-full bg-white rounded-br-lg"
                    title="Version preview"
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <div className="flex flex-1 items-center justify-center text-[#6c7086]">
                    <div className="text-center">
                      <History className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Select a version to preview</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-between border-t border-[#313244] px-5 py-3 shrink-0">
              <span className="text-xs text-[#6c7086]">
                {selectedVersion
                  ? `Version ${selectedVersion.version_number}${selectedVersion.is_current ? ' (current)' : ' selected'}`
                  : 'Select a version to preview or restore'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowVersionModal(false)}
                  className="rounded-md border border-[#45475a] px-3 py-1.5 text-sm text-[#cdd6f4] hover:bg-[#313244] transition-colors"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={restoreVersion}
                  disabled={!selectedVersion || !!selectedVersion.is_current || restoringVersion}
                  className="rounded-md bg-[#89b4fa] px-3 py-1.5 text-sm font-medium text-[#1e1e2e] hover:bg-[#a6c8ff] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {restoringVersion ? 'Restoring…' : 'Restore Version'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Send Test Modal ── */}
      {showTestModal && (
        <TestEmailTemplateModal
          templateId={String(id)}
          editorRef={editorRef}
          details={{ subject, preheader, fromEmail, fromName }}
          onClose={() => setShowTestModal(false)}
        />
      )}
    </div>
  );
}

// ─── Test Email Modal Component ─────────────────────────────────────────────
//
// Mirrors the campaign editor's test send: the live editor content goes up with
// the request, so what lands in the inbox is the draft on screen, saved or not.

function TestEmailTemplateModal({
  templateId,
  editorRef,
  details,
  onClose,
}: {
  templateId: string;
  editorRef: React.RefObject<EmailEditorHostHandle | null>;
  details: { subject: string; preheader: string; fromEmail: string; fromName: string };
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [testEmail, setTestEmail] = useState(user?.email ?? '');
  const toast = useToast();

  const sendMut = useMutation({
    mutationFn: () =>
      marketingSvc.sendTestEmailTemplate(templateId, {
        test_email: testEmail,
        subject: details.subject,
        preheader: details.preheader,
        from_email: details.fromEmail,
        from_name: details.fromName,
        html_content: editorRef.current?.getContent() ?? '',
        plain_content: editorRef.current?.getPlainContent() ?? '',
      }),
    onSuccess: () => {
      toast.success(`Test email sent to ${testEmail}`);
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message || 'Failed to send test email';
      toast.error(msg);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-gray-800">Send Test Email</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4">
          <label className="block text-sm font-medium text-gray-600 mb-1">Recipient Email</label>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter email address..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && testEmail.trim() && !sendMut.isPending) {
                e.preventDefault();
                sendMut.mutate();
              }
            }}
          />
          <p className="mt-2 text-xs text-gray-400">
            A test copy of this template — including unsaved edits — will be sent to the address
            above. The subject will be prefixed with [TEST] and personalization variables fall back
            to their defaults.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => sendMut.mutate()}
            disabled={!testEmail.trim() || sendMut.isPending}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {sendMut.isPending ? 'Sending…' : 'Send Test'}
          </button>
        </div>
      </div>
    </div>
  );
}
